// src/pages/QuickAddItemEK.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
  serverTimestamp,
  addDoc,
} from "firebase/firestore";
import { db } from "../services/firebase";
import { generateUniqueBarcode } from "../services/barcodeApi";

const DEFAULT_LOCATION_ID = "boutique";
const PAGE_SIZE = 40;

function useDebounced(value, delay = 250) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

function fmtAmount(n) {
  const v = Number(n || 0);
  return v.toLocaleString("fr-FR", { maximumFractionDigits: 0 }) + " XOF";
}

function fmtDate(ts) {
  if (!ts) return "—";
  try {
    const d = ts?.toDate?.() || new Date(ts);
    return d.toLocaleString();
  } catch {
    return "—";
  }
}

function stockDocId(locationId, itemId) {
  return `${locationId}__${itemId}`;
}

export default function QuickAddItemEK() {
  // Formulaire
  const [f, setF] = useState({
    name: "",
    barcode: "",
    price: "0",
    cost: "0",
    trackStock: true,
  });
  const [creating, setCreating] = useState(false);
  const [msg, setMsg] = useState("");
  const [barcodeLoading, setBarcodeLoading] = useState(false);

  const onChange = (e) =>
    setF((s) => ({
      ...s,
      [e.target.name]:
        e.target.type === "checkbox" ? e.target.checked : e.target.value,
    }));

  // Liste articles
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loadingList, setLoadingList] = useState(false);
  const [done, setDone] = useState(false);
  const [needle, setNeedle] = useState("");
  const debouncedNeedle = useDebounced(needle, 250);

  // Modal stock
  const [stockModal, setStockModal] = useState({
    open: false,
    item: null,
    loading: false,
    stock: null,
  });

  // --- Génération de code-barres pour ce formulaire ---
  const handleGenerateBarcode = async () => {
    setBarcodeLoading(true);
    try {
      const code = await generateUniqueBarcode({ prefix: "EK", digits: 10 });
      setF((s) => ({ ...s, barcode: code }));
      setMsg("✅ Code-barres généré et appliqué au champ UPC.");
    } catch (e) {
      console.error(e);
      setMsg("❌ " + (e.message || "Erreur génération code-barres"));
    } finally {
      setBarcodeLoading(false);
    }
  };

  // --- Création article ---
  // --- Création article ---
  async function submit(e) {
    e.preventDefault();
    if (!f.name.trim()) return;

    setCreating(true);
    setMsg("");
    try {
      // 1️⃣ Création dans itemsEK (POS / achats)
      const itemRef = await addDoc(collection(db, "itemsEK"), {
        name: f.name,
        nameLower: (f.name || "").toLowerCase(),
        barcode: f.barcode || null,
        price: Number(f.price || 0),
        cost: Number(f.cost || 0),
        trackStock: !!f.trackStock,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // 2️⃣ Création miroir dans winesek (catalogue vin)
      await addDoc(collection(db, "winesek"), {
        // champs que tu as déjà dans l’autre page
        name: f.name,
        price: Number(f.price || 0),
        category: "",          // ou "Wine" si tu préfères directement
        type: "",
        region: "",
        year: null,
        description: "",
        imageUrl: "",
        stock: 0,
        forSale: true,

        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),

        // petit lien pratique vers l’article POS
        sourceItemId: itemRef.id,
      });

      setMsg("✅ Article créé dans itemsEK et winesek : " + itemRef.id);
      setF({ name: "", barcode: "", price: "0", cost: "0", trackStock: true });
      await reloadList(true);
    } catch (e2) {
      console.error(e2);
      setMsg("❌ Erreur création: " + e2.message);
    } finally {
      setCreating(false);
    }
  }

  // async function submit(e) {
  //   e.preventDefault();
  //   if (!f.name.trim()) return;

  //   setCreating(true);
  //   setMsg("");
  //   try {
  //     const ref = await addDoc(collection(db, "itemsEK"), {
  //       name: f.name,
  //       nameLower: (f.name || "").toLowerCase(),
  //       barcode: f.barcode || null,
  //       price: Number(f.price || 0),
  //       cost: Number(f.cost || 0),
  //       trackStock: !!f.trackStock,
  //       createdAt: serverTimestamp(),
  //       updatedAt: serverTimestamp(),
  //     });

  //     setMsg("✅ Article créé : " + ref.id);
  //     setF({ name: "", barcode: "", price: "0", cost: "0", trackStock: true });
  //     await reloadList(true);
  //   } catch (e2) {
  //     console.error(e2);
  //     setMsg("❌ Erreur création: " + e2.message);
  //   } finally {
  //     setCreating(false);
  //   }
  // }

  // --- Chargement liste ---
  const reloadList = useCallback(
    async (reset = false) => {
      setLoadingList(true);
      if (reset) {
        setItems([]);
        setCursor(null);
        setDone(false);
      }
      try {
        let qy;
        const n = debouncedNeedle.trim().toLowerCase();

        if (n) {
          const start = n;
          const end = n + "\uf8ff";
          qy = query(
            collection(db, "itemsEK"),
            orderBy("nameLower"),
            where("nameLower", ">=", start),
            where("nameLower", "<=", end),
            limit(PAGE_SIZE)
          );
        } else {
          qy = query(
            collection(db, "itemsEK"),
            orderBy("nameLower"),
            limit(PAGE_SIZE)
          );
        }

        if (!reset && cursor) {
          qy = query(qy, startAfter(cursor));
        }

        const snap = await getDocs(qy);
        const rows = snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
          _doc: d,
        }));

        setItems((prev) => (reset ? rows : [...prev, ...rows]));
        if (snap.docs.length < PAGE_SIZE) {
          setDone(true);
          setCursor(null);
        } else {
          setDone(false);
          setCursor(snap.docs[snap.docs.length - 1]);
        }
      } catch (e) {
        console.error(e);
        setMsg("❌ Erreur chargement articles: " + e.message);
      } finally {
        setLoadingList(false);
      }
    },
    [debouncedNeedle, cursor]
  );

  useEffect(() => {
    reloadList(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    reloadList(true);
  }, [debouncedNeedle, reloadList]);

  const enrichedItems = useMemo(
    () =>
      items.map((it) => ({
        ...it,
        _priceLabel: fmtAmount(it.price || 0),
      })),
    [items]
  );

  // --- Fiche stock ---
  async function openStock(item) {
    if (!item?.id) return;
    setStockModal({
      open: true,
      item,
      loading: true,
      stock: null,
    });

    try {
      const sRef = doc(db, "stock", stockDocId(DEFAULT_LOCATION_ID, item.id));
      const snap = await getDoc(sRef);
      if (!snap.exists()) {
        setStockModal((prev) => ({
          ...prev,
          loading: false,
          stock: {
            qty: 0,
            updatedAt: null,
            locationId: DEFAULT_LOCATION_ID,
          },
        }));
      } else {
        setStockModal((prev) => ({
          ...prev,
          loading: false,
          stock: snap.data() || {
            qty: 0,
            updatedAt: null,
          },
        }));
      }
    } catch (e) {
      console.error(e);
      setStockModal((prev) => ({
        ...prev,
        loading: false,
        stock: { error: e.message },
      }));
    }
  }

  function closeStockModal() {
    setStockModal({
      open: false,
      item: null,
      loading: false,
      stock: null,
    });
  }

  // helper pour style d’alerte msg
  const msgClass = msg.startsWith("✅")
    ? "alert alert-success py-2 mt-2"
    : msg.startsWith("❌")
      ? "alert alert-danger py-2 mt-2"
      : msg
        ? "alert alert-info py-2 mt-2"
        : "";

  return (
    <div className="container-xxl py-3">
      <h4 className="mb-3">Articles (itemsEK) – Ajout rapide & stock</h4>

      {/* FORMULAIRE */}
      <div className="card mb-4 shadow-sm border-0">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-start mb-2">
            <div>
              <h5 className="mb-1">➕ Créer un article rapidement</h5>
              <p className="text-muted mb-0">
                Saisie rapide des produits utilisés par le POS et les PO.
              </p>
            </div>
            <small className="text-muted">Collection : itemsEK</small>
          </div>

          {msg && <div className={msgClass}>{msg.replace(/^✅ |^❌ /, "")}</div>}

          <form onSubmit={submit} className="mt-3">
            <div className="row g-3 align-items-end">
              {/* Nom */}
              <div className="col-12 col-md-4">
                <div className="form-floating">
                  <input
                    name="name"
                    className="form-control"
                    placeholder="Nom de l'article"
                    value={f.name}
                    onChange={onChange}
                    required
                  />
                  <label>Nom de l'article *</label>
                </div>
              </div>

              {/* Code-barres + Auto */}
              <div className="col-12 col-md-4">
                <label className="form-label small mb-1">
                  Code-barres / UPC
                </label>
                <div className="input-group">
                  <input
                    name="barcode"
                    className="form-control"
                    placeholder="EAN / UPC"
                    value={f.barcode}
                    onChange={onChange}
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={handleGenerateBarcode}
                    disabled={barcodeLoading}
                  >
                    {barcodeLoading ? (
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                      />
                    ) : (
                      "Auto"
                    )}
                  </button>
                </div>
                <div className="form-text">
                  Clique sur <strong>Auto</strong> pour générer un code-barres
                  unique.
                </div>
              </div>

              {/* Prix vente */}
              <div className="col-6 col-md-2">
                <div className="form-floating">
                  <input
                    name="price"
                    type="number"
                    className="form-control"
                    placeholder="Prix vente"
                    value={f.price}
                    onChange={onChange}
                    min="0"
                  />
                  <label>Prix vente (XOF)</label>
                </div>
              </div>

              {/* Coût achat */}
              <div className="col-6 col-md-2">
                <div className="form-floating">
                  <input
                    name="cost"
                    type="number"
                    className="form-control"
                    placeholder="Coût achat"
                    value={f.cost}
                    onChange={onChange}
                    min="0"
                  />
                  <label>Coût achat (XOF)</label>
                </div>
              </div>

              {/* Stock suivi */}
              <div className="col-12 col-md-2">
                <div className="form-check mt-2 pt-1">
                  <input
                    id="ts"
                    name="trackStock"
                    type="checkbox"
                    className="form-check-input"
                    checked={f.trackStock}
                    onChange={onChange}
                  />
                  <label htmlFor="ts" className="form-check-label small">
                    Suivre le stock
                  </label>
                </div>
              </div>
            </div>

            <div className="d-flex flex-wrap align-items-center mt-3 gap-2">
              <button className="btn btn-dark" type="submit" disabled={creating}>
                {creating ? (
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                  />
                ) : null}
                Créer l'article
              </button>

              <span className="ms-auto small text-muted">
                Ces articles sont utilisés par le POS & les PO (itemsEK).
              </span>
            </div>
          </form>
        </div>
      </div>

      {/* LISTE */}
      <div className="card shadow-sm">
        <div className="card-body">
          <div className="d-flex flex-wrap align-items-center mb-3 gap-2">
            <h6 className="m-0">Liste des articles</h6>
            <div className="ms-auto d-flex gap-2">
              <input
                className="form-control form-control-sm"
                style={{ minWidth: 220 }}
                placeholder="Recherche par nom…"
                value={needle}
                onChange={(e) => setNeedle(e.target.value)}
              />
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => reloadList(true)}
                disabled={loadingList}
              >
                {loadingList ? "Chargement…" : "Rafraîchir"}
              </button>
            </div>
          </div>

          <div className="table-responsive">
            <table className="table table-sm align-middle">
              <thead className="table-light">
                <tr>
                  <th>Article</th>
                  <th>Code-barres</th>
                  <th className="text-end">Prix</th>
                  <th className="text-center">Stock suivi</th>
                  <th className="text-center">Stock {DEFAULT_LOCATION_ID}</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {enrichedItems.map((it) => (
                  <tr key={it.id}>
                    <td>
                      <div className="fw-semibold">{it.name || "—"}</div>
                      <div className="small text-muted">
                        <code>{it.id}</code>
                      </div>
                    </td>
                    <td className="small">
                      {it.barcode ? (
                        <span>{it.barcode}</span>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td className="text-end small">
                      {fmtAmount(it.price || 0)}
                      {Number(it.cost || 0) > 0 && (
                        <div className="text-muted">
                          <small>Coût: {fmtAmount(it.cost || 0)}</small>
                        </div>
                      )}
                    </td>
                    <td className="text-center">
                      {it.trackStock ? (
                        <span className="badge bg-success-subtle text-success">
                          Oui
                        </span>
                      ) : (
                        <span className="badge bg-secondary-subtle text-muted">
                          Non
                        </span>
                      )}
                    </td>
                    <td className="text-center small">
                      <span className="text-muted">—</span>
                    </td>
                    <td className="text-end">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => openStock(it)}
                      >
                        Fiche stock
                      </button>
                    </td>
                  </tr>
                ))}
                {!enrichedItems.length && !loadingList && (
                  <tr>
                    <td colSpan={6} className="text-center text-muted py-3">
                      Aucun article trouvé.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="d-flex justify-content-center mt-2">
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={() => reloadList(false)}
              disabled={loadingList || done}
            >
              {loadingList
                ? "Chargement…"
                : done
                  ? "Fin de la liste"
                  : "Charger plus"}
            </button>
          </div>

          <div className="small text-muted mt-2">
            Total affiché : {enrichedItems.length} article(s)
          </div>
        </div>
      </div>

      {/* MODAL FICHE STOCK */}
      {stockModal.open && (
        <div
          className="modal fade show"
          style={{ display: "block", background: "rgba(0,0,0,0.4)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h6 className="modal-title">
                  Fiche de stock – {DEFAULT_LOCATION_ID}
                </h6>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeStockModal}
                />
              </div>
              <div className="modal-body">
                {!stockModal.item ? (
                  <div className="text-muted small">Aucune donnée article.</div>
                ) : (
                  <>
                    <div className="mb-2">
                      <div className="fw-semibold">
                        {stockModal.item.name || "Article"}
                      </div>
                      <div className="small text-muted">
                        <code>{stockModal.item.id}</code>
                        {stockModal.item.barcode && (
                          <> · CB: {stockModal.item.barcode}</>
                        )}
                      </div>
                    </div>
                    <div className="mb-2 small">
                      <div>Prix: {fmtAmount(stockModal.item.price || 0)}</div>
                      <div>Coût: {fmtAmount(stockModal.item.cost || 0)}</div>
                      <div>
                        Stock suivi :{" "}
                        {stockModal.item.trackStock ? "Oui" : "Non"}
                      </div>
                    </div>
                    <hr />
                    {stockModal.loading ? (
                      <div className="text-muted small">
                        Chargement stock…
                      </div>
                    ) : stockModal.stock?.error ? (
                      <div className="text-danger small">
                        Erreur: {stockModal.stock.error}
                      </div>
                    ) : (
                      <div className="small">
                        <div className="mb-1">
                          <span className="text-muted">Lieu :</span>{" "}
                          <strong>{DEFAULT_LOCATION_ID}</strong>
                        </div>
                        <div className="mb-1">
                          <span className="text-muted">
                            Quantité actuelle :
                          </span>{" "}
                          <strong>
                            {Number(
                              stockModal.stock?.qty || 0
                            ).toLocaleString()}
                          </strong>
                        </div>
                        <div className="mb-1">
                          <span className="text-muted">
                            Dernière mise à jour :
                          </span>{" "}
                          {fmtDate(stockModal.stock?.updatedAt)}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeStockModal}
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
